import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'
import { useSustainabilityApi } from '@/hooks/useApi/useSustainabilityApi/useSustainabilityApi'
import { useSustainabilityFiltersGetParams } from '@/features/Sustainability/hooks/useSustainabilityFiltersGetParams'
import type {
    TotalEmissionsReportSettings,
    TotalEmissionsReportType,
} from '@/hooks'
import { SustainabilityFiltersValues } from '@/utils/constants'

const MAX_CHART_DISPLAY_COUNT = 7
const MAX_COMMODITIES_COUNT = 6

export const useSustainabilityDisplayManager = (
    reportSettings: TotalEmissionsReportSettings,
    filtersValues: SustainabilityFiltersValues
) => {
    const currentType = reportSettings.type
    const selectedView = useSustainabilityStore(
        (state) => state.totalEmissions.selectedView
    )
    const { useTotalEmissionsQuery } = useSustainabilityApi()
    const filtersParams = useSustainabilityFiltersGetParams(
        filtersValues,
        reportSettings
    )

    const getParams = (type: TotalEmissionsReportType) => {
        const params = {
            ...filtersParams,
            ...reportSettings,
            type,
        }
        return params
    }

    const makeRequests = currentType !== 'electricitySources'

    const { data: commoditiesData } = useTotalEmissionsQuery(
        makeRequests
            ? getParams('commodities')
            : { 'nodeIds[]': '', type: 'commodities' }
    )
    const { data: emissionTypesData } = useTotalEmissionsQuery(
        makeRequests
            ? getParams('emissionTypes')
            : { 'nodeIds[]': '', type: 'emissionTypes' }
    )

    const getFilteredCount = (data?: { items?: { value: number }[] }) =>
        Array.isArray(data?.items)
            ? data.items.filter((item) => item.value !== 0).length
            : 0

    const commoditiesCount = getFilteredCount(commoditiesData)
    const emissionsCount = getFilteredCount(emissionTypesData)

    const getDisplayCount = (count: number) => {
        if (count > MAX_CHART_DISPLAY_COUNT) return MAX_COMMODITIES_COUNT
        return count
    }

    const displayCommoditiesCount = getDisplayCount(commoditiesCount)
    const displayEmissionsCount = getDisplayCount(emissionsCount)

    const showAlert =
        selectedView === 'chart' &&
        ((currentType === 'commodities' &&
            commoditiesCount > MAX_CHART_DISPLAY_COUNT) ||
            (currentType === 'emissionTypes' &&
                emissionsCount > MAX_CHART_DISPLAY_COUNT))

    return {
        showAlert,
        commoditiesCount: displayCommoditiesCount,
        emissionsCount: displayEmissionsCount,
    }
}
